import React from 'react'
import { Button, Tooltip } from 'antd'
import { BulbOutlined, BulbFilled } from '@ant-design/icons'
import { useTheme } from '../../utils/themeUtils'
import styles from './ThemeToggle.module.css'

interface ThemeToggleProps {
  className?: string
  size?: 'small' | 'middle' | 'large'
  type?: 'primary' | 'default' | 'dashed' | 'link' | 'text'
  showLabel?: boolean
}

/**
 * Theme Toggle Component
 *
 * A button component that toggles between light and dark themes.
 * Uses the useTheme hook to manage theme state.
 *
 * @param className - Additional CSS classes
 * @param size - Button size
 * @param type - Button type
 * @param showLabel - Whether to show text label alongside icon
 */
const ThemeToggle: React.FC<ThemeToggleProps> = ({
  className,
  size = 'middle',
  type = 'default',
  showLabel = false,
}) => {
  const { toggleTheme, isDark } = useTheme()

  const handleToggle = () => {
    toggleTheme()
  }

  const icon = isDark ? <BulbFilled /> : <BulbOutlined />
  const tooltipText = isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'
  const labelText = isDark ? 'Light Mode' : 'Dark Mode'

  return (
    <Tooltip title={!showLabel ? tooltipText : undefined}>
      <Button
        className={`${styles.themeToggle} ${isDark ? styles.dark : styles.light} ${
          className || ''
        }`}
        size={size}
        type="text"
        onClick={handleToggle}
        aria-label={tooltipText}>
        <span className={styles.iconWrapper}>{icon}</span>
        {showLabel && <span className={styles.label}>{labelText}</span>}
      </Button>
    </Tooltip>
  )
}

export default ThemeToggle
